import React, { useEffect } from "react";

//Hooks
import { useMutation, useQuery } from "react-query";
import { useHistory } from "react-router";
import { useOrderCancel, useOrderUpdate } from "../../../../hooks/api/fulfillment/orders";

//components
import { Tag, Popconfirm, Button, notification, Spin, Space, Badge, Tooltip, Modal, Timeline, Typography } from "antd";
import { ExceptionOutlined, LoadingOutlined } from "@ant-design/icons";
import { ShipmentTagDropdown } from "@secondcloset/web-components";
import FulfillmentBackButton from "../../FulfillmentBackButton";
import UpdateFulfilledFromButton from "./UpdateFulfilledFromButton";

// libs
import ShipmentTags from "../../../../lib/fulfillment/shipmentTags";
import Order, { getReturnableOrderItems } from "../../../../lib/fulfillment/order";
import { isEmpty, orderBy, startCase } from "lodash-es";
import Organization from "../../../../lib/organization";
import { Shipment } from "@secondcloset/fulfillment-utils";
import { isValidBulkOrderShipmentTag, ShipmentTag } from "./helpers";

//API
import { bulkUpdateOrders, Log } from "../../../../api/fulfillment/order";
import UpdateShippingServiceButton from "./UpdateShippingServiceButton";
import { Fulfillment } from "@secondcloset/types";
import { fetchInventoryTransfers } from "api/transfer";
import moment from "moment";

interface OrderHeaderProps {
  order: Fulfillment.Order<"V1" | "V2"> & { dispatch_state?: "enqueued"; logs?: Log[] };
  orderItems?: Fulfillment.OrderItem<"V2">[];
  shipments?: Fulfillment.Shipment<"V2">[];
  onRefetchOrder: () => unknown;
}

const OrderHeader: React.FC<OrderHeaderProps> = ({ order, orderItems, shipments, onRefetchOrder }) => {
  const history = useHistory();
  const orderID = order.id;
  const organizationID = order.organization.id;
  const isIKEA = Organization.isIKEA(order.organization);
  const isOrderCancelled = !!order?.cancelled;
  const { cancelOrder, orderCancelDetails } = useOrderCancel();
  const { updateOrder, orderUpdateDetails } = useOrderUpdate();
  const onError = (error: string) => {
    notification.error({
      message: "Error",
      description: error,
    });
  };

  const { mutateAsync: onUpdateShipmentReadyStatus, ...updateShipmentReadyStatus } = useMutation(bulkUpdateOrders, {
    onSuccess: () => {
      onRefetchOrder();
    },
    onError,
  });

  const error = orderCancelDetails.error || orderUpdateDetails.error;
  useEffect(() => {
    if (error) onError(error);
  }, [error]);

  const { data: transfers, ...transferQuery } = useQuery(
    ["transfer", order.external_order_number],
    () => fetchInventoryTransfers({ q: order.external_order_number, organization_id: order.organization.id }),
    { enabled: !!order.external_order_number }
  );

  const onCancelOrder = () => {
    cancelOrder(orderID).then(onRefetchOrder);
  };

  const renderCancelButton = () => {
    if (isOrderCancelled)
      return (
        <Tag style={{ minHeight: "auto" }} color="red">
          Cancelled
        </Tag>
      );
    let title = "Are you sure you want to cancel the order?";
    if (transfers?.data.length) title = "This order is associated with an inventory transfer. " + title;

    return (
      <Popconfirm placement="topLeft" title={title} onConfirm={onCancelOrder} okText="Yes" cancelText="No">
        <Button loading={orderCancelDetails.loading || transferQuery.isLoading} danger>
          Cancel Order
        </Button>
      </Popconfirm>
    );
  };

  const renderReturnItemsButton = () => {
    const returnableItems = orderItems?.length ? getReturnableOrderItems(orderItems) : Order.getReturnableItems(order);
    const link = `/fulfillment/orders/${orderID}/create-shipment?isReturn=true`;
    return (
      <Button type="primary" onClick={() => history.push(link)} disabled={isEmpty(returnableItems) || isIKEA}>
        Return Items
      </Button>
    );
  };

  const onReadyStatusUpdate = (shipmentTag: Exclude<ShipmentTag, "customer_booking" | "internal_booking">) => {
    if (!isValidBulkOrderShipmentTag(shipmentTag) || !orderID) return;
    const body = { order_ids: [orderID], shipment_tag: shipmentTag };
    onUpdateShipmentReadyStatus(body);
  };

  const renderShipmentTag = (shipmentTag: ShipmentTag) => {
    return (
      <Tag key={shipmentTag} color={ShipmentTags.getShipmentTagColor(shipmentTag)}>
        {ShipmentTags.getShipmentTagText(shipmentTag)}
      </Tag>
    );
  };

  const renderShipmentTagDropdown = (shipmentTag: ShipmentTag) => {
    return (
      <ShipmentTagDropdown
        shipmentTag={shipmentTag as Exclude<ShipmentTag, "customer_booking" | "internal_booking">}
        loading={updateShipmentReadyStatus.status === "loading"}
        onReadyStatusUpdate={onReadyStatusUpdate}
      />
    );
  };

  const renderShipmentTags = () => {
    const shipmentTags = [...order.shipment_tags] as Array<ShipmentTag>;
    if (shipments && Shipment.hasInternalBooking(shipments)) shipmentTags.push("internal_booking");

    const shouldShowShipmentDropdown = (tag: ShipmentTag): boolean => {
      if (isOrderCancelled) return false;
      if (order.has_errors && tag === "unfulfilled_items") return false;
      return isValidBulkOrderShipmentTag(tag);
    };

    return (
      <div className="shipment-tags">
        Shipment Tags:
        {shipmentTags.map((t) => (shouldShowShipmentDropdown(t) ? renderShipmentTagDropdown(t) : renderShipmentTag(t)))}
      </div>
    );
  };

  const renderEditFulfilledFrom = () => {
    const shipmentTags = [...order.shipment_tags] as Array<ShipmentTag>;
    if (orderUpdateDetails.loading) {
      const antIcon = <LoadingOutlined spin />;
      return <Spin indicator={antIcon} />;
    }
    return (
      <UpdateFulfilledFromButton
        refetchOrder={onRefetchOrder}
        updateOrder={updateOrder}
        fulfilledFrom={order?.fulfilled_from}
        orderID={orderID}
        loading={orderUpdateDetails.loading}
        shipmentTags={shipmentTags}
      />
    );
  };

  const showLogs = () => {
    return Modal.info({
      title: "Order Orchestration Results & Dispatch Exception Logs",
      content: (
        <Timeline reverse mode="left" style={{ margin: "1em 0" }}>
          {orderBy(order.logs, [(o) => o.updated_at], ["asc"])?.map((log) => (
            <Timeline.Item
              key={log.id}
              label={moment(log.updated_at).format("lll")}
              color={log.event === "order_dispatch" ? "red" : "blue"}
            >
              <Space direction="vertical" size={0}>
                <Typography.Text>{log.description || "N/A"}</Typography.Text>
                <Typography.Text type="secondary">{startCase(log.event)}</Typography.Text>
              </Space>
            </Timeline.Item>
          ))}
        </Timeline>
      ),
    });
  };

  return (
    <div className="order-header">
      <div className="header-buttons">
        <FulfillmentBackButton
          urlPath={`/organizations/${organizationID}`}
          label={`Back to ${order.organization.name}`}
        />
        <Space>
          {!!order.logs?.length && <Button icon={<ExceptionOutlined />} onClick={showLogs} />}
          {renderReturnItemsButton()}
          {renderCancelButton()}
        </Space>
      </div>
      <div className="order-id-with-edit-btn">
        <Space>
          <h1 className="foid">Order ID: {order?.order_id}</h1>
          {order.dispatch_state === "enqueued" && (
            <Tooltip title="This order is in the dispatch queue">
              <Badge status="processing" />
            </Tooltip>
          )}
        </Space>
        <Space>
          {renderEditFulfilledFrom()}
          <UpdateShippingServiceButton order={order} onUpdateShippingServiceSuccess={onRefetchOrder} />
        </Space>
      </div>
      {renderShipmentTags()}
    </div>
  );
};

export default OrderHeader;
